import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TID } from "@/constants/testIds";
import { authApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyOtp() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(45);
  const email = sessionStorage.getItem("sa_login_email") || "";

  useEffect(() => {
    if (!email) navigate("/login", { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [seconds]);

  const submit = async (code) => {
    setLoading(true);
    try {
      const res = await authApi.verifyOtp({ email, otp: code });
      if (res?.token) {
        login(res.token, res.user || { name: "Superadmin", email, role: "superadmin" });
        toast.success("Verified", { description: "Welcome back to CliniQueue." });
        sessionStorage.removeItem("sa_login_email");
        navigate("/dashboard", { replace: true });
      } else {
        toast.error("Invalid code", { description: "Please try again." });
      }
    } catch (err) {
      toast.error("Verification failed", {
        description: err?.response?.data?.message || "The code is invalid or expired.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (otp.length !== 8) {
      toast.error("Enter all 8 digits");
      return;
    }
    submit(otp);
  };

  const resend = () => {
    setSeconds(45);
    toast.success("Code resent", { description: "A new code was sent to your registered email." });
  };

  return (
    <div className="lux-grain relative flex min-h-screen items-center justify-center bg-background px-6">
      <div className="absolute right-5 top-5 z-20">
        <ThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="glass relative z-10 w-full max-w-md rounded-3xl p-8 sm:p-10"
      >
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-lux-emerald/15 text-lux-emerald">
          <MailCheck className="h-7 w-7" />
        </div>

        <h1 className="font-heading text-3xl font-semibold tracking-tight">Verify your identity</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter the 8-digit code we sent to your registered email.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="flex justify-center px-4" data-testid={TID.otpInput}>
            <div className="mx-auto w-full max-w-[520px] min-w-[280px] rounded-3xl border border-border/50 bg-secondary/30 p-5 sm:p-6 shadow-[0_0_0_1px_rgba(255,255,255,0.06)]">
              <div className="mb-4 text-center text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                Enter your 8-digit verification code
              </div>
              <InputOTP maxLength={8} value={otp} onChange={setOtp}>
                <InputOTPGroup className="grid w-full grid-cols-4 gap-3 justify-items-center sm:grid-cols-8">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className="h-14 w-14 rounded-2xl border border-input bg-background text-xl font-semibold text-white shadow-sm"
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          <Button
            data-testid={TID.otpSubmit}
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-full bg-lux-emerald text-base font-semibold text-[#0a2417] transition-colors hover:bg-lux-emeraldHover"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify & sign in"}
          </Button>
        </form>

        <div className="mt-6 flex items-center justify-between text-sm">
          <button
            data-testid={TID.otpBack}
            onClick={() => navigate("/login")}
            className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {seconds > 0 ? (
            <span className="text-muted-foreground">Resend in {seconds}s</span>
          ) : (
            <button
              data-testid={TID.otpResend}
              onClick={resend}
              className="font-semibold text-gold transition-colors hover:text-lux-gold"
            >
              Resend code
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
